import { Component, OnInit, NgZone } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@app/shared/services/state/state.service';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { ProductService } from '@app/shared/services/product/product.service';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { ProductImageMatteService } from '@app/shared/services/design-huddle/product-image-matte.service';
import { DesignHuddleUploadOverrideService } from '@app/shared/services/design-huddle/design-huddle-upload-override.service';
import { ProjectTitlesDialogComponent, toDesignHuddleProjectTitle } from '@app/shared/components/master-product/project-titles-dialog/project-titles-dialog.component';
import { environment } from '@env/environment';
import { getRoutePrefix } from '@app/shared/utils/route-prefix.util';
import { pickEditBaseSide, resolveSideAlias } from '@app/shared/constants/decoration-location.constants';
import { decorationSizeLines } from '@app/shared/utils/decoration-size';

declare var DSHDLib: any;

@Component({
  selector: 'app-edit-master-product',
  templateUrl: './edit-master-product.component.html',
  styleUrls: ['./edit-master-product.component.css']
})
export class EditMasterProductComponent implements OnInit {
  storeId: number;
  storeName: string;
  editProductData: any = null;
  designData: any = null;
  product: any = null; // The single product being edited
  decorationSizeLines: string[] = [];
  masterProductImgBaseURL: string = environment.masterProductImgBaseURL;
  imgBaseURL: string = environment.imgBaseURL;

  // Product display
  productCurrentImage: string = '';
  productImageList: { url: string, sideName: string, isPlain: boolean }[] = [];
  selectedStyleId: number;
  isLoadingPreview: boolean = false;

  // Preview cache: cacheKey (sideName) -> generated preview URL
  generatedPreviews: Map<string, string> = new Map();

  // Sizes for current style
  currentSizes: any[] = [];

  // Product description, labels, tags (like product-detail page)
  productLabels: any[] = [{ text: '', backgroundColor: '', textColor: '' }];
  productTags: any[] = [{ tagtext: '' }];
  longDescription: string = '';

  // Editor modal
  showEditorModal: boolean = false;
  isCreatingProject: boolean = false;
  editorProductName: string = '';
  editorProjectId: string | null = null;
  editorSideName: string = 'front';
  currentProductImageUrl: string = '';

  // Saved preview from DB
  savedPreviewUrl: string = '';

  // Saved style previews map: styleId -> preview URL
  savedStylePreviews: { [styleId: number]: string } = {};

  // Saved customizations per side from DB: sideName -> customization object
  savedSideCustomizations: { [sideName: string]: any } = {};

  // Only show colors that were saved/selected for this design
  selectedStyles: any[] = [];

  // CustomProductImage data (same as product-detail page's imageListAll)
  allProductImages: { Id: number, Image: string, IsDefault: boolean, StyleId: number, UniqueStyleId: number }[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private stateService: StateService,
    private loader: LoaderService,
    private notification: NotificationService,
    private productService: ProductService,
    private designHuddleService: DesignHuddleService,
    private productImageMatteService: ProductImageMatteService,
    private uploadOverride: DesignHuddleUploadOverrideService,
    private ngZone: NgZone
  ) {
    this.route.queryParams.subscribe(params => {
      this.storeId = params['storeId'] || localStorage.getItem('storeId');
      this.storeName = params['storeName'] || localStorage.getItem('storeName') || '';
    });
  }

  ngOnInit(): void {
    // Must be in edit mode
    if (!this.stateService.isEditMode()) {
      this.notification.error('No product selected for editing', 'Error');
      const prefix = getRoutePrefix(this.router.url);
      if (prefix === '/store') {
        this.router.navigate(['/store/list']);
      } else {
        this.router.navigate(['/superadmin/storelist']);
      }
      return;
    }

    this.editProductData = this.stateService.getEditProductData();
    this.designData = this.stateService.getDesignData();

    if (!this.editProductData || !this.designData) {
      this.notification.error('Missing edit data. Please try again.', 'Error');
      this.goBack();
      return;
    }

    const listItem = this.editProductData.listItem;
    this.decorationSizeLines = decorationSizeLines(
      this.editProductData.productDetails,
      listItem?.DecorationName || listItem?.CustomizationType
    );

    // Initialize DesignHuddle SDK
    this.initializeDesignHuddle();

    // Load the product from master product API
    this.loadProduct();
  }

  initializeDesignHuddle(): void {
    if (typeof (window as any).DSHDLib !== 'undefined') {
      (window as any).DSHDLib.configure({
        domain: environment.designHuddleDomain,
        client_id: environment.designHuddleClientId,
        guest: true
      });
    }
  }

  loadProduct(): void {
    this.loader.show();

    const productId = this.editProductData.ProductId;
    const filterModel = {
      Skip: 0,
      Take: 10,
      Size: [],
      Brand: [],
      Color: [],
      InkSoftUserId: 0,
      searchBy: '',
      Category: '',
      UserId: Number(localStorage.getItem('userId')),
      ProductIds: [productId]
    };

    this.productService.getAllProducts(filterModel).subscribe((res: any) => {
      if (res.StatusCode === 200 && res.Data?.ProductList) {
        // Find the specific product being edited
        const found = res.Data.ProductList.find((p: any) => p.ID === productId);
        if (found) {
          const activeStyles = found.Styles.filter((s: any) => s.IsActive);
          this.product = {
            ProductId: found.ID,
            CategoryId: found.CategoryUniqueId,
            StyleId: found.DefaultStyleId,
            Description: found.Name,
            Styles: activeStyles,
            ImageUrl: this.getImageUrl(activeStyles[0]?.ImageFilePath_Front || ''),
            UnitCost: this.getLowestUnitPrice(activeStyles),
            Sizes: this.getSizes(activeStyles, found.DefaultStyleId),
            IsActive: found.IsActive,
            CustomizationType: found.CustomizationType,
            Sides: found.Sides || [],
            LongDescription: found.LongDescription || ''
          };

          // Build selected colors from SavedStyles (customized product details API)
          const productDetails = this.editProductData.productDetails;
          const savedStyles = productDetails?.SavedStyles || productDetails?.StyleList || [];

          savedStyles.forEach((ss: any) => {
            const ssId = Number(ss.UniqueStyleId || ss.StyleId);
            if (ss.PngRelativeUrlWithProduct) {
              this.savedStylePreviews[ssId] = this.getImageUrl(ss.PngRelativeUrlWithProduct);
            }
          });

          // Extract per-side customizations from CustomizedProducts (from DB)
          const customizedProducts = productDetails?.CustomizedProducts || [];
          customizedProducts.forEach((cp: any) => {
            const sideName = resolveSideAlias(cp.SideName || '', this.product?.Sides);
            if (sideName && cp.Customizations) {
              this.savedSideCustomizations[sideName] = typeof cp.Customizations === 'string'
                ? JSON.parse(cp.Customizations) : cp.Customizations;
            }
          });

          // Use SavedStyles data directly for selected colors display
          // Enrich with master product style data (HtmlColor1, Sizes, image paths)
          this.selectedStyles = savedStyles.map((ss: any) => {
            const ssId = ss.UniqueStyleId || ss.StyleId;
            const masterStyle = activeStyles.find((s: any) => s.ID === ssId);
            return {
              ID: ssId,
              Name: ss.StyleName || masterStyle?.Name || '',
              HtmlColor1: ss.ColorCode || masterStyle?.HtmlColor1 || '',
              ImageFilePath_Front: ss.ImageFilePath_Front || masterStyle?.ImageFilePath_Front || '',
              ImageFilePath_Back: ss.ImageFilePath_Back || masterStyle?.ImageFilePath_Back || '',
              ImageFilePath_SleeveLeft: ss.ImageFilePath_SleeveLeft || masterStyle?.ImageFilePath_SleeveLeft || '',
              ImageFilePath_SleeveRight: ss.ImageFilePath_SleeveRight || masterStyle?.ImageFilePath_SleeveRight || '',
              PngRelativeUrlWithProduct: ss.PngRelativeUrlWithProduct || '',
              IsActive: ss.IsActive !== undefined ? ss.IsActive : true,
              Sizes: masterStyle?.Sizes || [],
              // Carry forward all saved DB fields
              AzureBlobPrintFileUrl: ss.AzureBlobPrintFileUrl || '',
              AzureBlobThumbnailUrl: ss.AzureBlobThumbnailUrl || '',
              DesignHuddleProjectId: ss.DesignHuddleProjectId || '',
              DesignHuddleTemplateId: ss.DesignHuddleTemplateId || '',
              DesignHuddleJobId: ss.DesignHuddleJobId || '',
              Customizations: ss.Customizations || null,
              ElementPlacement: ss.ElementPlacement || '',
              ZonePlacement: ss.ZonePlacement || '',
              ProductMetadata: ss.ProductMetadata || '',
              Tags: ss.Tags || '',
              AlternateDesigns: ss.AlternateDesigns || [],
              PerSideImages: ss.PerSideImages || []
            };
          });

          // Fallback: if no saved styles, show all active styles
          if (this.selectedStyles.length === 0) {
            this.selectedStyles = activeStyles;
          }

          // Set the saved/locked style from CustomizedProduct.StyleId
          // Priority: list item StyleId (from CustomizedProduct table) > CP StyleId from details API > DefaultStyleId
          const cpStyleId = customizedProducts?.[0]?.StyleId;
          this.selectedStyleId = Number(this.editProductData.StyleId ?? cpStyleId ?? found.DefaultStyleId);
          console.log('🔒 Locked style determination:', {
            listItemStyleId: this.editProductData.StyleId,
            cpStyleId,
            defaultStyleId: found.DefaultStyleId,
            resolvedSelectedStyleId: this.selectedStyleId,
            allCpStyleIds: customizedProducts?.map((cp: any) => ({ id: cp.CustomizedProductId, styleId: cp.StyleId, side: cp.SideName }))
          });
          // Load description, labels, tags from productDetails (like product-detail page)
          this.longDescription = productDetails?.Description || this.product.LongDescription || '';
          const labelData = productDetails?.ProductMetadata || null;
          const tagData = productDetails?.Tags || null;
          if (labelData) {
            try {
              this.productLabels = JSON.parse(labelData);
            } catch (e) {
              this.productLabels = [{ text: '', backgroundColor: '', textColor: '' }];
            }
          }
          if (tagData) {
            try {
              this.productTags = JSON.parse(tagData);
            } catch (e) {
              this.productTags = tagData.split(',').map((t: string) => ({ tagtext: t.trim() }));
            }
          }

          // Use CustomProductImage data passed via state from the WithStyle response (ResponseData1).
          // The WithStyle endpoint now returns the same image list as the non-WithStyle endpoint,
          // so we no longer need a second API call.
          if (this.editProductData.allProductImages && this.editProductData.allProductImages.length > 0) {
            this.allProductImages = this.editProductData.allProductImages;
          }
          this.initActiveStyle();
        } else {
          this.loader.hide();
          this.notification.error('Product not found', 'Error');
          this.goBack();
        }
      } else {
        this.loader.hide();
      }
    }, (err: any) => {
      this.loader.hide();
      this.notification.error('Failed to load product', 'Error');
    });
  }

  setActiveStyle(styleId: number): void {
    if (!this.product) return;

    // Find from both selectedStyles (saved DB data) and product.Styles (master product data)
    const savedStyle = this.selectedStyles.find((s: any) => Number(s.ID) === Number(styleId));
    const masterStyle = this.product.Styles.find((s: any) => Number(s.ID) === Number(styleId));

    if (!savedStyle && !masterStyle) return;

    this.isLoadingPreview = true;
    this.product.StyleId = Number(styleId);
    this.selectedStyleId = Number(styleId);

    // Clear preview cache when style changes
    this.generatedPreviews.clear();

    // Update current sizes for this style
    const sizes = masterStyle?.Sizes || savedStyle?.Sizes || [];
    this.currentSizes = (sizes || [])
      .sort((a: any, b: any) => (a.SortOrder || 0) - (b.SortOrder || 0))
      .map((s: any) => ({ ...s, AvailableQty: null }));

    // Build image list — priority order:
    // 1. CustomProductImage from API (same source as product-detail page)
    // 2. DesignHuddle thumbnails from buildSidesFromDB
    // 3. Plain master product images (ImageFilePath_Front etc)
    this.productImageList = [];

    // Priority 1: CustomProductImage data (loaded from getCustomizedProductDetailsByProductId API)
    const styleImages = this.getImagesByStyle(Number(styleId));
    if (styleImages.length > 0) {
      styleImages.forEach((img: any) => {
        if (!img.Image) return;
        this.productImageList.push({
          url: img.Image,
          sideName: 'front',
          isPlain: false
        });
      });
    }

    // Priority 2: DesignHuddle thumbnails from DB
    if (this.productImageList.length === 0) {
      const sides = this.buildSidesFromDB(Number(styleId));
      if (sides.length > 0) {
        sides.forEach((side: any) => {
          const thumbnailUrl = side.ThumbnailUrl || side.ThumbnailRelativeUrl || '';
          if (!thumbnailUrl) return;
          const fullUrl = thumbnailUrl.startsWith('http')
            ? thumbnailUrl
            : this.joinUrl(this.imgBaseURL, thumbnailUrl);
          this.productImageList.push({
            url: fullUrl,
            sideName: side.SideName,
            isPlain: false
          });
        });
      }
    }

    // Priority 3: plain product images from master product
    if (this.productImageList.length === 0) {
      const style = masterStyle || savedStyle;
      if (style) {
        const sideConfigs = [
          { field: 'ImageFilePath_Front', sideName: 'front' },
          { field: 'ImageFilePath_Back', sideName: 'back' },
          { field: 'ImageFilePath_SleeveLeft', sideName: 'left sleeve' },
          { field: 'ImageFilePath_SleeveRight', sideName: 'right sleeve' }
        ];
        sideConfigs.forEach(cfg => {
          if (!style[cfg.field]) return;
          const plainUrl = style[cfg.field].startsWith('http')
            ? style[cfg.field]
            : this.joinUrl(this.imgBaseURL, style[cfg.field]);
          this.productImageList.push({
            url: plainUrl,
            sideName: cfg.sideName,
            isPlain: true
          });
        });
      }
    }

    // Set main image: saved preview > first thumbnail
    const savedPreview = this.savedStylePreviews[Number(styleId)];
    if (savedPreview) {
      this.productCurrentImage = savedPreview;
    } else if (this.productImageList.length > 0) {
      this.productCurrentImage = this.productImageList[0].url;
    } else if (savedStyle?.PngRelativeUrlWithProduct) {
      this.productCurrentImage = this.getImageUrl(savedStyle.PngRelativeUrlWithProduct);
    }
  }

  /** Initialize active style and preview after images are loaded */
  private initActiveStyle(): void {
    this.loader.hide();
    this.setActiveStyle(this.selectedStyleId);

    // Store saved preview URL for reference
    const listItem = this.editProductData.listItem;
    if (this.savedStylePreviews[this.selectedStyleId]) {
      this.savedPreviewUrl = this.savedStylePreviews[this.selectedStyleId];
    } else if (listItem?.PngRelativeUrlWithProduct) {
      this.savedPreviewUrl = this.getImageUrl(listItem.PngRelativeUrlWithProduct);
    }

    // Auto-generate front preview with design overlay for the initial color
    const frontItem = this.productImageList.find(img => img.sideName === 'front') || this.productImageList[0];
    if (frontItem) {
      this.selectImage(frontItem);
    }
  }

  /** Get CustomProductImage entries filtered by style (same as product-detail's mapImageForCustomizeProductByStyle) */
  getImagesByStyle(uniqueStyleId: number): { Id: number, Image: string, IsDefault: boolean }[] {
    const seen = new Set<string>();
    let filtered = this.allProductImages.filter(x => {
      if (x.UniqueStyleId != uniqueStyleId) return false;
      if (seen.has(x.Image)) return false;
      seen.add(x.Image);
      return true;
    });
    // Prefer DH/Azure mockups over plain InkSoft images
    const dhMockups = filtered.filter(x => x.Id >= 0 && x.Image && !x.Image.startsWith(this.imgBaseURL));
    if (dhMockups.length > 0) {
      filtered = filtered.filter(x => x.Id === -99 || (x.Id >= 0 && !x.Image.startsWith(this.imgBaseURL)));
    }
    return filtered;
  }

  selectStyle(style: any): void {
    this.setActiveStyle(Number(style.ID));
  }

  isStyleSelected(styleId: any): boolean {
    return Number(styleId) === Number(this.selectedStyleId);
  }

  onImageLoaded(): void {
    this.isLoadingPreview = false;
  }

  // On thumbnail click: just show that side's plain image for the selected color
  selectImage(item: { url: string, sideName: string, isPlain: boolean }): void {
    this.isLoadingPreview = true;
    this.productCurrentImage = item.url;
  }

  // Find the CustomizedProduct entry from DB that matches a given side name
  getCustomizedProductForSide(sideName: string): any {
    const productDetails = this.editProductData?.productDetails;
    const customizedProducts = productDetails?.CustomizedProducts || [];
    return customizedProducts.find((cp: any) =>
      resolveSideAlias(cp.SideName || '', this.product?.Sides) === resolveSideAlias(sideName, this.product?.Sides)
    );
  }

  /**
   * Calculate media_position from elementPlacement and zonePlacement.
   * scale_width = element.width / zone.width
   * x = (element.left + element.width / 2 - zone.left) / zone.width
   * y = (element.top + element.height / 2 - zone.top) / zone.height
   */
  calculateMediaPosition(elementPlacement: any, zonePlacement: any): any {
    if (!elementPlacement || !zonePlacement) return null;
    if (!zonePlacement.width || !zonePlacement.height) return null;

    const scale_width = elementPlacement.width / zonePlacement.width;
    const x = (elementPlacement.left + elementPlacement.width / 2 - zonePlacement.left) / zonePlacement.width;
    const y = (elementPlacement.top + elementPlacement.height / 2 - zonePlacement.top) / zonePlacement.height;

    return { scale_width, x, y };
  }

  /**
   * Extract logo URL from DB Customizations JSON.
   * Customizations format: { classes: { logo: { url: "..." } } }
   */
  extractLogoUrl(customizations: any): string | null {
    if (!customizations) return null;
    const parsed = typeof customizations === 'string' ? JSON.parse(customizations) : customizations;
    return parsed?.classes?.logo?.url || null;
  }

  // Generate preview for a side using getVariableTemplatePreviewURL with DB data
  generateSidePreview(sideName: string, templateId: any, productImageUrl: string, designUrl: string, cacheKey: string): void {
    const sdk = (window as any).DSHDLib;
    if (!sdk) {
      this.productCurrentImage = productImageUrl;
      return;
    }

    const numericTemplateId = parseInt(templateId);
    const sideNameLower = sideName.toLowerCase();

    // Get DB data for this side
    const cpEntry = this.getCustomizedProductForSide(sideNameLower);
    const selectedStyle = this.selectedStyles.find((s: any) => s.ID === this.selectedStyleId);

    // Extract logo URL from DB Customizations (priority: side CP > style > fallback designUrl)
    const savedCustomization = this.savedSideCustomizations[sideNameLower];
    const styleCustomization = selectedStyle?.Customizations;
    const logoUrl = this.extractLogoUrl(savedCustomization)
      || this.extractLogoUrl(styleCustomization)
      || designUrl;

    // Parse ElementPlacement and ZonePlacement from DB
    let elementPlacement: any = null;
    let zonePlacement: any = null;

    if (cpEntry?.ElementPlacement) {
      try {
        elementPlacement = typeof cpEntry.ElementPlacement === 'string'
          ? JSON.parse(cpEntry.ElementPlacement) : cpEntry.ElementPlacement;
      } catch (e) { }
    }
    if (cpEntry?.ZonePlacement) {
      try {
        zonePlacement = typeof cpEntry.ZonePlacement === 'string'
          ? JSON.parse(cpEntry.ZonePlacement) : cpEntry.ZonePlacement;
      } catch (e) { }
    }

    // Calculate media_position from element and zone placements
    const mediaPosition = this.calculateMediaPosition(elementPlacement, zonePlacement);

    // Build logo customization with media_position
    const logo: any = { url: logoUrl };
    if (mediaPosition) {
      logo.media_position = mediaPosition;
    }
    const customizationObject = { classes: { logo } };

    // Zone dimensions and offset — try PrintPlacement from product.Sides, then ZonePlacement
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    const printPlacement = this.getSidePrintPlacement(sideNameLower);
    if (printPlacement) {
      try {
        const pp = typeof printPlacement === 'string' ? JSON.parse(printPlacement) : printPlacement;
        if (pp.Width && pp.Height) zoneDimensions = `${pp.Width}x${pp.Height}`;
        if (pp.X !== undefined && pp.Y !== undefined) zoneOffset = `${pp.X}x${pp.Y}`;
      } catch (e) { }
    } else if (zonePlacement) {
      if (zonePlacement.width && zonePlacement.height) {
        zoneDimensions = `${zonePlacement.width}x${zonePlacement.height}`;
      }
      if (zonePlacement.left !== undefined && zonePlacement.top !== undefined) {
        zoneOffset = `${zonePlacement.left}x${zonePlacement.top}`;
      }
    }

    this.isLoadingPreview = true;

    // Flatten the transparent product image onto white BEFORE handing it to DH — see
    // ProductImageMatteService for why (DH has no background/matte param of its own).
    this.productImageMatteService.getFlattenedProductImageUrl(productImageUrl).subscribe(flattenedProductImageUrl => {
      sdk.getVariableTemplatePreviewURL(
        {
          template_id: numericTemplateId,
          customizations: customizationObject,
          // REVERTED to 300/500 (original) — see comment on the equivalent revert in
          // product-color-selection.component.ts's generateSidePreview (zone-calibration).
          width: 300,
          product_image_url: flattenedProductImageUrl,
          product_image_width: 500,
          product_image_canvas_zone_dimensions: zoneDimensions,
          product_image_canvas_zone_offset: zoneOffset,
          // No saved placement → top-anchor the design in the print zone (chest print).
          // When a media_position exists it's user intent, so leave the default centering.
          ...(mediaPosition ? {} : { product_image_canvas_zone_vertical_align: 'top' }),
          fit: 'center'
        },
        (error: any, url: string) => {
          this.ngZone.run(() => {
            this.isLoadingPreview = false;

            if (error) {
              console.error('Preview generation error:', error);
              this.productCurrentImage = productImageUrl;
            } else {
              this.generatedPreviews.set(cacheKey, url);
              this.productCurrentImage = url;
            }
          });
        }
      );
    });
  }

  displayColor(colorCode: string): any {
    return { 'background-color': colorCode };
  }

  getImageUrl(relativeUrl: string): string {
    if (!relativeUrl) return '';
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
      return relativeUrl;
    }
    return this.joinUrl(this.imgBaseURL, relativeUrl);
  }

  /** Join base URL with path, ensuring exactly one slash between them */
  joinUrl(base: string, path: string): string {
    if (!path) return base;
    const trimmedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const trimmedPath = path.startsWith('/') ? path : '/' + path;
    return trimmedBase + trimmedPath;
  }

  getLowestUnitPrice(styles: any[]): number {
    let lowest = Infinity;
    styles.forEach(style => {
      if (style.Sizes) {
        style.Sizes.forEach((size: any) => {
          if (size.UnitPrice < lowest) lowest = size.UnitPrice;
        });
      }
    });
    return lowest === Infinity ? 0 : lowest;
  }

  getSizes(styles: any[], defaultStyleId: number): any[] {
    const style = styles.find(s => s.ID === defaultStyleId) || styles[0];
    if (!style?.Sizes) return [];
    return style.Sizes.sort((a: any, b: any) => a.SortOrder - b.SortOrder).map((s: any) => ({
      Name: s.Name,
      LongName: s.LongName
    }));
  }

  // Open editor — uses current thumbnail's product image and DB customizations (same as product-color-selection)
  openEditor(): void {
    if (!this.product) return;

    const sdk = (window as any).DSHDLib;
    if (!sdk) {
      this.notification.error('DesignHuddle SDK not loaded', 'Error');
      return;
    }

    // Determine current side from the active thumbnail
    const activeThumbnail = this.productImageList.find(item => {
      const cacheKey = `${this.selectedStyleId}_${item.sideName}`;
      return this.productCurrentImage === item.url ||
             this.generatedPreviews.get(cacheKey) === this.productCurrentImage;
    }) || this.productImageList[0];
    const sideName = activeThumbnail?.sideName || 'front';
    this.editorSideName = sideName;

    // Use the actual thumbnail URL directly (already has masterProductImgBaseURL prefix)
    const productImageUrl = activeThumbnail?.url || '';
    this.currentProductImageUrl = productImageUrl;

    const selectedStyle = this.selectedStyles.find((s: any) => s.ID === this.selectedStyleId);

    // Get DB data for this side
    const cpEntry = this.getCustomizedProductForSide(sideName);

    // TemplateId from DB or product.Sides
    let templateId = cpEntry?.DesignHuddleTemplateId;
    if (!templateId && this.product?.Sides) {
      const side = this.product.Sides.find((s: any) =>
        s.Name?.toLowerCase() === sideName || s.DisplayName?.toLowerCase() === sideName
      );
      templateId = side?.TemplateId;
    }
    if (!templateId) {
      this.notification.warning('No template found for this product side', 'Warning');
      return;
    }

    // Build customization from DB — use full saved object, add media_position
    const designUrl = this.designData?.designUrl || this.designData?.previewUrl || '';
    const savedCustomization = this.savedSideCustomizations[sideName];
    const styleCustomization = selectedStyle?.Customizations;

    // Parse ElementPlacement and ZonePlacement from DB
    let elementPlacement: any = null;
    let zonePlacement: any = null;
    if (cpEntry?.ElementPlacement) {
      try {
        elementPlacement = typeof cpEntry.ElementPlacement === 'string'
          ? JSON.parse(cpEntry.ElementPlacement) : cpEntry.ElementPlacement;
      } catch (e) { }
    }
    if (cpEntry?.ZonePlacement) {
      try {
        zonePlacement = typeof cpEntry.ZonePlacement === 'string'
          ? JSON.parse(cpEntry.ZonePlacement) : cpEntry.ZonePlacement;
      } catch (e) { }
    }

    const mediaPosition = this.calculateMediaPosition(elementPlacement, zonePlacement);

    // Use the full DB customization object directly (priority: side CP > style > fallback)
    let customizationObject: any;
    if (savedCustomization) {
      customizationObject = JSON.parse(JSON.stringify(savedCustomization));
    } else if (styleCustomization) {
      const parsed = typeof styleCustomization === 'string' ? JSON.parse(styleCustomization) : styleCustomization;
      customizationObject = JSON.parse(JSON.stringify(parsed));
    } else {
      customizationObject = { classes: { logo: { url: designUrl } } };
    }

    // Inject media_position into the logo if calculated
    if (mediaPosition && customizationObject?.classes?.logo) {
      customizationObject.classes.logo.media_position = mediaPosition;
    }

    // Zone dimensions and offset from ZonePlacement
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    if (zonePlacement) {
      if (zonePlacement.width && zonePlacement.height) {
        zoneDimensions = `${zonePlacement.width}x${zonePlacement.height}`;
      }
      if (zonePlacement.left !== undefined && zonePlacement.top !== undefined) {
        zoneOffset = `${zonePlacement.left}x${zonePlacement.top}`;
      }
    }

    const projectTitle = this.editProductData.productDetails?.Name || this.product.Description;
    this.editorProductName = this.product.Description + ' - ' + sideName;
    this.showEditorModal = true;
    this.isCreatingProject = true;

    // Check if existing project from DB
    const existingProjectId = cpEntry?.DesignHuddleProjectId;

    // Helper to insert editor (setTimeout lets Angular render the *ngIf container)
    const insertEditor = (projectId: string) => {
      setTimeout(() => {
        sdk.Editors.insert('dh-edit-editor-container', {
          project_id: projectId,
          product_image_url: productImageUrl,
          product_image_width: 500,
          product_image_canvas_zone_dimensions: zoneDimensions,
          product_image_canvas_zone_offset: zoneOffset
        }, (insertErr: any, instance: any) => {
          if (insertErr) {
            console.error('❌ [edit-master-product] Editors.insert callback error:', insertErr);
            return;
          }
          // Override DH's own Upload-button flow so in-editor uploads run through the same
          // transparent-padding trim pipeline as the upload-design page (see service doc).
          if (instance) {
            this.uploadOverride.wireMediaUploadOverride(instance);
          }
        });
        this.ngZone.run(() => {
          this.isCreatingProject = false;
        });
      }, 100);
    };

    if (existingProjectId) {
      // Reuse existing project from DB
      this.editorProjectId = existingProjectId;
      insertEditor(existingProjectId);
    } else {
      // Create new project with DB customizations
      sdk.createProject({
        template_id: parseInt(templateId),
        project_title: toDesignHuddleProjectTitle(projectTitle),
        customizations: customizationObject
      }, (err: any, projectData: any) => {
        this.ngZone.run(() => {
          if (err) {
            this.isCreatingProject = false;
            this.showEditorModal = false;
            this.notification.error('Failed to create editor project', 'Error');
            return;
          }
          this.editorProjectId = projectData.project_id;
          insertEditor(projectData.project_id);
        });
      });
    }
  }

  // Close editor, fetch metadata, refresh preview with updated media_position
  closeEditor(): void {
    const projectId = this.editorProjectId;
    const sideName = this.editorSideName;

    this.showEditorModal = false;

    // Remove all editor content from the container
    const container = document.getElementById('dh-edit-editor-container');
    if (container) {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    }

    if (projectId && sideName) {
      // Fetch updated metadata from editor and refresh preview
      this.isLoadingPreview = true;
      this.designHuddleService.getProjectData(projectId).subscribe({
        next: (response: any) => {
          const parsedData = this.parseProjectMetadata(response, projectId);
          const mediaPosition = parsedData
            ? this.calculateMediaPosition(parsedData.elementPlacement, parsedData.zonePlacement)
            : null;

          // Update saved customizations with new placement data
          if (parsedData?.elementPlacement) {
            const cpEntry = this.getCustomizedProductForSide(sideName);
            if (cpEntry) {
              cpEntry.ElementPlacement = JSON.stringify(parsedData.elementPlacement);
              if (parsedData.zonePlacement) {
                cpEntry.ZonePlacement = JSON.stringify(parsedData.zonePlacement);
              }
            }
          }

          this.refreshPreviewAfterEditor(sideName, mediaPosition);
        },
        error: (err: any) => {
          console.error('Failed to fetch editor metadata:', err);
          this.refreshPreviewAfterEditor(sideName, null);
        }
      });
    }

    // Clear editor state
    this.editorProjectId = null;
    this.editorSideName = 'front';
  }

  /**
   * Parse project metadata to extract element and zone placement from the logo element.
   */
  parseProjectMetadata(metadata: any, projectId: string): any {
    const root = metadata?.pages ? metadata : (metadata?.data || metadata);
    const pages = root?.pages || [];
    if (pages.length === 0) return null;

    for (const page of pages) {
      const elements = page.elements || {};
      for (const elementId of Object.keys(elements)) {
        const element = elements[elementId];
        const classes = element.element_classes || [];
        if (classes.includes('logo')) {
          const sideData: any = {
            projectId: projectId,
            elementPlacement: {
              height: element.height,
              width: element.width,
              left: element.left,
              top: element.top
            },
            zonePlacement: null
          };
          if (element.zone) {
            sideData.zonePlacement = {
              height: element.zone.height,
              width: element.zone.width,
              left: element.zone.left,
              top: element.zone.top
            };
          }
          return sideData;
        }
      }
    }
    return null;
  }

  /**
   * Refresh preview after editor close using updated media_position.
   */
  refreshPreviewAfterEditor(sideName: string, mediaPosition: any): void {
    const sdk = (window as any).DSHDLib;
    if (!sdk) {
      this.isLoadingPreview = false;
      return;
    }

    // Get logo URL
    const designUrl = this.designData?.designUrl || this.designData?.previewUrl || '';
    const savedCustomization = this.savedSideCustomizations[sideName];
    const selectedStyle = this.selectedStyles.find((s: any) => s.ID === this.selectedStyleId);
    const styleCustomization = selectedStyle?.Customizations;
    const logoUrl = this.extractLogoUrl(savedCustomization)
      || this.extractLogoUrl(styleCustomization)
      || designUrl;

    // Build customization with updated media_position
    const logo: any = { url: logoUrl };
    if (mediaPosition) {
      logo.media_position = mediaPosition;
    }
    const customizationObject = { classes: { logo } };

    // Get templateId
    const cpEntry = this.getCustomizedProductForSide(sideName);
    let templateId = cpEntry?.DesignHuddleTemplateId;
    if (!templateId && this.product?.Sides) {
      const side = this.product.Sides.find((s: any) =>
        s.Name?.toLowerCase() === sideName || s.DisplayName?.toLowerCase() === sideName
      );
      templateId = side?.TemplateId;
    }
    if (!templateId) {
      this.isLoadingPreview = false;
      return;
    }

    // Zone dimensions from ZonePlacement
    let zoneDimensions = '300x300';
    let zoneOffset = '100x100';
    let zonePlacement: any = null;
    if (cpEntry?.ZonePlacement) {
      try {
        zonePlacement = typeof cpEntry.ZonePlacement === 'string'
          ? JSON.parse(cpEntry.ZonePlacement) : cpEntry.ZonePlacement;
      } catch (e) { }
    }
    if (zonePlacement) {
      if (zonePlacement.width && zonePlacement.height) {
        zoneDimensions = `${zonePlacement.width}x${zonePlacement.height}`;
      }
      if (zonePlacement.left !== undefined && zonePlacement.top !== undefined) {
        zoneOffset = `${zonePlacement.left}x${zonePlacement.top}`;
      }
    }

    // Clear old cache for this side
    const cacheKey = `${this.selectedStyleId}_${sideName}`;
    this.generatedPreviews.delete(cacheKey);

    // Flatten the transparent product image onto white BEFORE handing it to DH — see
    // ProductImageMatteService for why (DH has no background/matte param of its own). Only the
    // flattened copy used for THIS preview call changes — this.currentProductImageUrl itself is
    // left untouched since the live editor (Editors.insert) also reads it and a checkerboard
    // there is the correct UX cue for transparency while designing.
    this.productImageMatteService.getFlattenedProductImageUrl(this.currentProductImageUrl).subscribe(flattenedProductImageUrl => {
      sdk.getVariableTemplatePreviewURL(
        {
          template_id: parseInt(templateId),
          customizations: customizationObject,
          // REVERTED to 300/500 (original) — see comment on the equivalent revert in
          // product-color-selection.component.ts's generateSidePreview (zone-calibration).
          width: 300,
          product_image_url: flattenedProductImageUrl,
          product_image_width: 500,
          product_image_canvas_zone_dimensions: zoneDimensions,
          product_image_canvas_zone_offset: zoneOffset,
          // No saved placement → top-anchor the design in the print zone (chest print).
          // When a media_position exists it's user intent, so leave the default centering.
          ...(mediaPosition ? {} : { product_image_canvas_zone_vertical_align: 'top' }),
          fit: 'center'
        },
        (error: any, url: string) => {
          this.ngZone.run(() => {
            this.isLoadingPreview = false;
            if (error) {
              console.error('Preview refresh error:', error);
            } else {
              this.generatedPreviews.set(cacheKey, url);
              this.productCurrentImage = url;
            }
          });
        }
      );
    });
  }

  /**
   * Build per-style Sides array from DB CustomizedProducts data.
   * For the given styleId, looks up per-style data from each CP's SavedStyles
   * (which comes from ProductStyleAlternateDesign table).
   * Falls back to CP-level data (default design) if no per-style entry exists.
   */
  buildSidesFromDB(styleId: number): any[] {
    const productDetails = this.editProductData?.productDetails;
    const customizedProducts = productDetails?.CustomizedProducts || [];

    const parseCustomizations = (val: any) => {
      if (!val) return null;
      return typeof val === 'string' ? JSON.parse(val) : val;
    };

    // Collect ALL SavedStyles from all CPs into one list
    // (ProductStyleMappings all point to the first CP, so only the first CP has SavedStyles)
    const allSavedStyles: any[] = [];
    customizedProducts.forEach((cp: any) => {
      (cp.SavedStyles || []).forEach((ss: any) => {
        allSavedStyles.push(ss);
      });
    });

    // Get all entries for this specific style
    const styleEntries = allSavedStyles.filter((ss: any) => {
      const ssId = Number(ss.UniqueStyleId || ss.StyleId);
      return ssId === Number(styleId);
    });

    // Collect all AlternateDesigns from styleEntries (one per side from ProductStyleAlternateDesign)
    const firstEntry = styleEntries[0];
    const alternateDesigns: any[] = firstEntry?.AlternateDesigns || [];
    const hasAlternateDesign = alternateDesigns.length > 0;

    let sides: any[];

    if (hasAlternateDesign) {
      // Style has ProductStyleAlternateDesign entries — iterate over those directly.
      // We must NOT iterate over customizedProducts here: the locked style may have a different
      // number of sides than this style (e.g. locked=1 side, Royal=2 sides or vice versa).
      // Each alternateDesign entry already carries its own SideName.
      sides = alternateDesigns.map((perStyleData: any) => {
        // SideName may now hold a decoration-location name ("Full Front") instead of a technical
        // identifier — resolveSideAlias() reverse-maps it back so Sides-table/image-field
        // matching below keeps working regardless of which form is stored.
        const sideName = resolveSideAlias(perStyleData.SideName || 'front', this.product?.Sides);
        // Look up the corresponding CP for TemplateId (CP SideName may differ in case)
        const matchingCp = customizedProducts.find((cp: any) =>
          resolveSideAlias(cp.SideName || 'front', this.product?.Sides) === sideName
        );
        const perStyleThumbnail = perStyleData?.PngRelativeUrlWithProduct || null;

        return {
          SideName: sideName,
          ProjectId: perStyleData?.DesignHuddleProjectId || matchingCp?.DesignHuddleProjectId || null,
          DownloadUrl: perStyleData?.AzureBlobPrintFileUrl || matchingCp?.AzureBlobPrintFileUrl || null,
          ThumbnailUrl: perStyleThumbnail || matchingCp?.AzureBlobThumbnailUrl || null,
          ThumbnailRelativeUrl: perStyleData?.ThumbnailRelativeUrl || perStyleThumbnail || matchingCp?.ThumbnailRelativeUrl || '',
          PageId: '',
          JobId: perStyleData?.DesignHuddleJobId || matchingCp?.DesignHuddleJobId || '',
          TemplateId: matchingCp?.DesignHuddleTemplateId || perStyleData?.DesignHuddleTemplateId || null,
          PrintPlacement: this.getSidePrintPlacement(sideName),
          ElementPlacement: perStyleData?.ElementPlacement || matchingCp?.ElementPlacement || null,
          ZonePlacement: perStyleData?.ZonePlacement || matchingCp?.ZonePlacement || null,
          Customizations: parseCustomizations(perStyleData?.Customizations) || parseCustomizations(matchingCp?.Customizations) || null,
          // AlternateDesigns entries don't carry their own InkSoftLocationName yet — only the
          // top-level CustomizedProductEntry does. Raw passthrough only; the "if not present, use
          // the standard-side constant" fallback happens once, centrally, when the save payload
          // is built (product-color-selection.component.ts).
          InkSoftLocationName: matchingCp?.InkSoftLocationName || null
        };
      });
    } else {
      // No alternate design → iterate over CPs (the locked/default style path).
      // Per-side images from CustomProductImage provide thumbnails for same-design replicas.
      const perSideImages: any[] = firstEntry?.PerSideImages || [];
      sides = customizedProducts.map((cp: any, cpIndex: number) => {
        const sideName = resolveSideAlias(cp.SideName || 'front', this.product?.Sides);
        const sideImage = perSideImages[cpIndex] || null;
        const perSideThumbnail = sideImage?.ImageName || null;
        // Only fall back to CP-level thumbnail if this IS the locked/original style
        // For other styles, don't show the default color's thumbnail (wrong color product image)
        const isLockedStyle = Number(cp.StyleId) === Number(styleId);
        const fallbackThumbnail = isLockedStyle
          ? (cp.AzureBlobThumbnailUrl || cp.ThumbnailRelativeUrl || null)
          : null;

        return {
          SideName: sideName,
          ProjectId: cp.DesignHuddleProjectId || null,
          DownloadUrl: cp.AzureBlobPrintFileUrl || null,
          ThumbnailUrl: perSideThumbnail || fallbackThumbnail || null,
          ThumbnailRelativeUrl: perSideThumbnail || (isLockedStyle ? cp.ThumbnailRelativeUrl : '') || '',
          PageId: '',
          JobId: cp.DesignHuddleJobId || '',
          TemplateId: cp.DesignHuddleTemplateId || null,
          PrintPlacement: this.getSidePrintPlacement(sideName),
          ElementPlacement: cp.ElementPlacement || null,
          ZonePlacement: cp.ZonePlacement || null,
          Customizations: parseCustomizations(cp.Customizations) || null,
          // Raw passthrough of whatever's persisted today (may be null/legacy) — the "if not
          // present, use the standard-side constant" fallback happens once, centrally, when the
          // save payload is built (product-color-selection.component.ts).
          InkSoftLocationName: cp.InkSoftLocationName || null
        };
      });
    }

    return sides;
  }

  /**
   * Get PrintPlacement for a side from product.Sides
   */
  getSidePrintPlacement(sideName: string): any {
    if (!this.product?.Sides) return null;
    const side = this.product.Sides.find((s: any) =>
      (s.Name || s.DisplayName || '').toLowerCase() === sideName
    );
    return side?.PrintPlacement || null;
  }

  /**
   * Get TemplateId for a side — from product.Sides or DB
   */
  getSideTemplateId(sideName: string): any {
    // From DB CustomizedProducts
    const cpEntry = this.getCustomizedProductForSide(sideName);
    if (cpEntry?.DesignHuddleTemplateId) return cpEntry.DesignHuddleTemplateId;

    // From product.Sides (master product API)
    if (this.product?.Sides) {
      const side = this.product.Sides.find((s: any) =>
        (s.Name || s.DisplayName || '').toLowerCase() === sideName
      );
      if (side?.TemplateId) return side.TemplateId;
    }
    return null;
  }

  // Continue to color selection — show project title dialog, then build full payload from DB data
  continueToColorSelection(): void {
    if (!this.product) return;

    const productDetails = this.editProductData.productDetails;
    const currentProjectTitle = productDetails?.Name || this.product.Description;

    // Show project title dialog so user can edit or confirm the name.
    // isEditMode: true tells the dialog to prefill the input with the existing title.
    const dialogData = {
      title: 'Enter Project Title',
      message: 'Please provide a title for this project:',
      storeId: this.storeId,
      isEditMode: true,
      products: [{
        productId: this.product.ProductId,
        productName: this.product.Description,
        projectTitle: currentProjectTitle,
        disabled: false
      }]
    };

    const dialogRef = this.dialog.open(ProjectTitlesDialogComponent, {
      width: '600px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirmed) {
        const projectTitle = result.projectTitles[0] || currentProjectTitle;
        this.proceedToColorSelection(projectTitle);
      }
    });
  }

  private proceedToColorSelection(projectTitle: string): void {
    this.loader.show('Loading product…', 'Preparing your product for editing.');

    const productDetails = this.editProductData.productDetails;
    const customizedProducts = productDetails?.CustomizedProducts || [];
    const savedStyles = productDetails?.SavedStyles || [];

    // Build Sides from DB for the default/locked style
    const defaultSides = this.buildSidesFromDB(this.selectedStyleId);

    // Ensure Sides have TemplateId from product.Sides if not in DB
    defaultSides.forEach((side: any) => {
      if (!side.TemplateId) {
        side.TemplateId = this.getSideTemplateId(side.SideName);
      }
    });

    // Get first CP's ProjectId as default
    const defaultProjectId = customizedProducts[0]?.DesignHuddleProjectId || this.editorProjectId || null;

    // Build the product data matching create flow structure
    const productData = {
      ...this.product,
      ProductId: this.product.ProductId,
      Description: this.product.Description,
      CustomizationType: this.product.CustomizationType,
      ProjectId: defaultProjectId,
      ProjectTitle: projectTitle,
      ProjectName: projectTitle,
      selectedStyleId: this.selectedStyleId,
      PreviewImageUrl: this.savedPreviewUrl || this.productCurrentImage,
      // The side the saved design is actually on (front when it has one) - not always 'front'.
      currentSideName: pickEditBaseSide(defaultSides.map((side: any) => side.SideName)),
      Sides: defaultSides,
      Customizations: defaultSides[0]?.Customizations || null,
      TemplateId: defaultSides[0]?.TemplateId || null,
      JobId: defaultSides[0]?.JobId || '',
      // Pass SavedStyles so product-color-selection can pre-select all saved colors with full side data
      _editSavedStyles: savedStyles,
      _editCustomizedProducts: customizedProducts,
      // Preserve master product sides (from API/sides table) separately so the color-selection
      // page can build the full sides dropdown — defaultSides only contains previously-published
      // sides (from CustomizedProducts), which may be a subset of the product's sides.
      masterProductSides: this.product.Sides || [],
      // Pass CustomProductImage data so color-selection uses correct image URLs (not 80px GIF)
      _allProductImages: this.allProductImages
    };

    // Store in state (same as create flow)
    this.stateService.setSelectedProductsForColors([productData]);
    this.stateService.setDesignData(this.designData);
    this.stateService.setDesignDataForProduct(this.designData);

    // Do NOT hide the loader here. Keep it visible through the route transition so
    // there is no blank gap between this page and product-color-selection's ngOnInit.
    // initializeSingleSideFlow() on the next page will call loader.show() with its
    // own message, which naturally supersedes this one. The loader is eventually
    // hidden by that page once preview generation completes.

    const prefix = getRoutePrefix(this.router.url);
    this.router.navigate([`${prefix}/product-color-selection`], {
      queryParams: {
        storeId: this.storeId,
        storeName: this.storeName
      }
    });
  }

  goBack(): void {
    this.stateService.clearEditMode();
    const prefix = getRoutePrefix(this.router.url);
    if (prefix === '/store') {
      this.router.navigate(['/store/publishedproducts']);
    } else {
      this.router.navigate(['/superadmin/shopify-products', this.storeId, this.storeName]);
    }
  }
}
